import { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, TextField, MenuItem, Chip, Button, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Divider,
} from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import UndoIcon from "@mui/icons-material/Undo";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Swal from "sweetalert2";
import CampoMoeda from "../../components/CampoMoeda";
import {
  listarFinanceiro, listarEventos, confirmarPagamentoManual, estornarPagamentoManual, abrirComprovante,
  comprovacaoPagamento,
} from "../../services/eventoService";

const moeda = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const fmtDT = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const FORMAS = {
  dinheiro: "Dinheiro",
  credito: "Cartão de crédito",
  debito: "Cartão de débito",
  pix: "PIX",
  isencao: "Isenção",
};

// "Pago" fica reservado para inscrição que teve cobrança e foi quitada. Evento sem taxa
// nasce com Pago = true e valor zero — rotulá-lo de "Pago (PIX)" sugeria um recebimento
// que nunca existiu.
const Linha = ({ rotulo, valor }) => (
  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
    <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
    <Typography variant="body2" sx={{ textAlign: "right", wordBreak: "break-all" }}><b>{valor}</b></Typography>
  </Stack>
);

const situacao = (c) =>
  c.Status === "X" ? { label: "Cancelada", color: "default" }
    : Number(c.ValorTotal || 0) <= 0 ? { label: "Sem taxa", color: "info" }
      : c.Pago ? { label: c.BaixaManual ? "Pago (manual)" : "Pago (PIX)", color: "success" }
        : { label: "Aguardando pagamento", color: "warning" };

export default function FinanceiroList() {
  const [eventos, setEventos] = useState([]);
  const [cobrancas, setCobrancas] = useState([]);
  const [idEvento, setIdEvento] = useState("");
  const [situ, setSitu] = useState("pendente");
  const [termo, setTermo] = useState("");
  const [carregando, setCarregando] = useState(false);
  // Paginação no servidor; os totais vêm calculados sobre o filtro inteiro, não sobre a página.
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(50);
  const [total, setTotal] = useState(0);
  const [totais, setTotais] = useState({ recebido: 0, pendente: 0 });
  const [salvando, setSalvando] = useState(null);

  // Diálogo de baixa
  const [baixa, setBaixa] = useState(null); // cobrança em confirmação
  const [dataPg, setDataPg] = useState(hojeISO());
  const [forma, setForma] = useState("pix");
  const [arquivo, setArquivo] = useState(null);
  const [comprovacao, setComprovacao] = useState(null);   // evidência do pagamento
  const [valorPago, setValorPago] = useState(0);   // recebido de fato (pode ser menor: desconto)
  const [observacao, setObservacao] = useState("");

  useEffect(() => { listarEventos().then((r) => setEventos(r.data)).catch(() => { }); }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await listarFinanceiro({
        idEvento, situacao: situ, termo, pagina: pagina + 1, porPagina,
      });
      setCobrancas(data.itens || []);
      setTotal(data.total || 0);
      setTotais({ recebido: data.totalRecebido || 0, pendente: data.totalPendente || 0 });
    }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setCarregando(false); }
  }, [idEvento, situ, termo, pagina, porPagina]);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [idEvento, situ, pagina, porPagina]);
  // Trocar filtro volta à primeira página, senão a tela pode abrir vazia.
  useEffect(() => { setPagina(0); }, [idEvento, situ]);

  const abrirBaixa = (c) => {
    setBaixa(c); setDataPg(hojeISO()); setForma("pix"); setArquivo(null);
    setValorPago(Number(c.ValorTotal || 0));   // começa no valor cheio
    setObservacao("");
  };

  // Diferença entre o cobrado e o recebido = desconto concedido nesta baixa.
  const descontoBaixa = () => Number(baixa?.ValorTotal || 0) - Number(valorPago || 0);

  const verComprovacao = async (c) => {
    try { setComprovacao((await comprovacaoPagamento(c.Id)).data); }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  const confirmar = async () => {
    if (!forma) { Swal.fire("Atenção", "Escolha a forma de pagamento.", "warning"); return; }
    const desc = descontoBaixa();
    if (Number(valorPago) < 0) { Swal.fire("Atenção", "O valor recebido não pode ser negativo.", "warning"); return; }
    if (desc < 0) { Swal.fire("Atenção", "O valor recebido não pode ser maior que o da inscrição.", "warning"); return; }
    // Diferença sem justificativa vira divergência de caixa sem explicação depois.
    if (desc > 0 && !observacao.trim()) {
      Swal.fire("Atenção", "Informe o motivo do valor menor (ex.: desconto concedido).", "warning");
      return;
    }
    setSalvando(baixa.Id);
    try {
      const { data } = await confirmarPagamentoManual(baixa.Id, {
        dataPagamento: dataPg, formaPagamento: forma, comprovante: arquivo,
        valorPago, observacao: observacao.trim(),
      });
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: data.mensagem, showConfirmButton: false, timer: 1600 });
      setBaixa(null);
      carregar();
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setSalvando(null); }
  };

  const estornar = async (c) => {
    const r = await Swal.fire({
      title: "Estornar pagamento?",
      html: `A cobrança de <b>${c.Responsavel || "—"}</b> voltará para <b>aguardando pagamento</b> e o comprovante será removido.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Estornar", cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    setSalvando(c.Id);
    try {
      const { data } = await estornarPagamentoManual(c.Id);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: data.mensagem, showConfirmButton: false, timer: 1600 });
      carregar();
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setSalvando(null); }
  };

  const verComprovante = async (c) => {
    try { await abrirComprovante(c.Id); }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || "Não foi possível abrir o comprovante.", "error"); }
  };

  const totalRecebido = totais.recebido;
  const totalPendente = totais.pendente;

  return (
    <Box>
      <Typography variant="h5" mb={2}>Financeiro — pagamentos</Typography>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Evento" value={idEvento} onChange={(e) => setIdEvento(e.target.value)} sx={{ minWidth: 260 }}>
          <MenuItem value="">Todos os eventos</MenuItem>
          {eventos.map((e) => <MenuItem key={e.Id} value={e.Id}>{e.Nome}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Situação" value={situ} onChange={(e) => setSitu(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="pendente">Aguardando pagamento</MenuItem>
          <MenuItem value="pago">Pagas</MenuItem>
          <MenuItem value="cancelada">Canceladas</MenuItem>
        </TextField>
        <TextField size="small" label="Buscar (responsável, CPF)" value={termo}
          onChange={(e) => setTermo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && carregar()} sx={{ minWidth: 280 }} />
        <Button variant="outlined" onClick={carregar}>Buscar</Button>
      </Stack>

      <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
        <Chip label={`Cobranças: ${total}`} />
        <Chip color="success" variant="outlined" label={`Recebido: ${moeda(totalRecebido)}`} />
        <Chip color="warning" variant="outlined" label={`Pendente: ${moeda(totalPendente)}`} />
      </Stack>

      <Paper>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Responsável / CPF</TableCell>
                <TableCell>Participantes</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cobrancas.map((c) => {
                const st = situacao(c);
                return (
                  <TableRow key={c.Id} hover>
                    <TableCell>{c.Evento}</TableCell>
                    <TableCell>{c.Responsavel}<br /><Typography variant="caption" color="text.secondary">{c.Cpf}</Typography></TableCell>
                    <TableCell>{c.Participantes || "—"}</TableCell>
                    <TableCell align="right">
                      {moeda(c.ValorTotal)}
                      {/* Recebido a menor: mostra o desconto, senão a diferença some da vista. */}
                      {c.Pago && c.ValorPago != null && Number(c.ValorPago) < Number(c.ValorTotal) && (
                        <Tooltip title={c.ObservacaoBaixa || "Baixa com valor menor"}>
                          <Typography variant="caption" color="warning.main" display="block">
                            recebido {moeda(c.ValorPago)}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" label={st.label} color={st.color} variant={c.Pago ? "filled" : "outlined"} /></TableCell>
                    <TableCell>
                      {/* Sem taxa não tem meio de pagamento — mostrar "PIX" ali era invenção. */}
                      {Number(c.ValorTotal || 0) <= 0 ? (
                        <Typography variant="caption" color="text.secondary">Sem cobrança</Typography>
                      ) : c.Pago ? (
                        <>
                          <Typography variant="caption" display="block">
                            {c.BaixaManual ? (FORMAS[c.FormaPagamento] || "Manual") : "PIX"} • {fmtDT(c.DtPagamento)}
                          </Typography>
                          {c.UsuarioBaixa && <Typography variant="caption" color="text.secondary">por {c.UsuarioBaixa}</Typography>}
                        </>
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {c.Status !== "X" && !c.Pago && (
                          <Button size="small" variant="outlined" color="success" startIcon={<PaidIcon />}
                            disabled={salvando === c.Id} onClick={() => abrirBaixa(c)} sx={{ textTransform: "none" }}>
                            Confirmar
                          </Button>
                        )}
                        {c.Pago && (
                          <Tooltip title="Ver os dados que comprovam este pagamento">
                            <Button size="small" variant="text" startIcon={<FactCheckIcon />}
                              onClick={() => verComprovacao(c)} sx={{ textTransform: "none" }}>
                              Comprovação
                            </Button>
                          </Tooltip>
                        )}
                        {c.TemComprovante && (
                          <Tooltip title="Ver comprovante">
                            <Button size="small" variant="text" startIcon={<ReceiptLongIcon />}
                              onClick={() => verComprovante(c)} sx={{ textTransform: "none" }}>
                              Comprovante
                            </Button>
                          </Tooltip>
                        )}
                        {c.BaixaManual && (
                          <Tooltip title="Estornar (desfazer confirmação manual)">
                            <Button size="small" variant="text" color="inherit" startIcon={<UndoIcon />}
                              disabled={salvando === c.Id} onClick={() => estornar(c)} sx={{ textTransform: "none" }}>
                              Estornar
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!cobrancas.length && (
                <TableRow><TableCell colSpan={7} align="center">
                  {carregando ? "Carregando…" : "Nenhuma cobrança encontrada."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={pagina}
          onPageChange={(_, p) => setPagina(p)}
          rowsPerPage={porPagina}
          onRowsPerPageChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      {/* Diálogo de confirmação de pagamento */}
      {/* Comprovação do pagamento — o que responde a uma contestação */}
      <Dialog open={!!comprovacao} onClose={() => setComprovacao(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Comprovação do pagamento</DialogTitle>
        <DialogContent dividers>
          {comprovacao && (
            <Stack spacing={1.2}>
              <Linha rotulo="Candidato" valor={`${comprovacao.Candidato || "—"} (${comprovacao.Cpf || "—"})`} />
              <Linha rotulo="Origem da baixa" valor={comprovacao.Origem} />
              {comprovacao.BaixaManual && <Linha rotulo="Registrada por" valor={comprovacao.UsuarioBaixa || "—"} />}
              {comprovacao.BaixaManual && <Linha rotulo="Forma informada" valor={FORMAS[comprovacao.FormaPagamento] || "—"} />}
              {comprovacao.ObservacaoBaixa && <Linha rotulo="Observação" valor={comprovacao.ObservacaoBaixa} />}

              <Divider />
              <Linha rotulo="Valor da inscrição" valor={moeda(comprovacao.ValorTotal)} />
              <Linha rotulo="Valor recebido" valor={moeda(comprovacao.PixValorRecebido ?? comprovacao.ValorPago)} />

              <Divider />
              <Linha rotulo="txid (cobrança)" valor={comprovacao.PixTxid || "—"} />
              <Linha rotulo="EndToEndId (transação)" valor={comprovacao.PixEndToEndId || "—"} />
              <Linha rotulo="Horário no banco" valor={fmtDT(comprovacao.PixHorarioBanco)} />
              <Linha rotulo="Detectado pelo sistema" valor={fmtDT(comprovacao.DtDeteccao)} />

              {comprovacao.PixEndToEndId ? (
                <Alert severity="success">
                  Com o <b>EndToEndId</b> o banco localiza a transação no extrato e identifica quem pagou.
                </Alert>
              ) : (
                <Alert severity="info">
                  Esta baixa é anterior ao registro da comprovação (ou foi manual), então não há
                  EndToEndId guardado. Use o <b>txid</b> para consultar no banco.
                </Alert>
              )}

              {comprovacao.PixRetorno && (
                <>
                  <Divider />
                  <Typography variant="caption" color="text.secondary">Retorno do banco:</Typography>
                  <Box component="pre" sx={{
                    m: 0, p: 1.5, bgcolor: "#f5f7fa", borderRadius: 1, fontSize: 11,
                    maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
                  }}>{comprovacao.PixRetorno}</Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComprovacao(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!baixa} onClose={() => setBaixa(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar pagamento</DialogTitle>
        <DialogContent dividers>
          {baixa && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Box>
                <Typography variant="body2"><b>{baixa.Responsavel}</b> — {baixa.Cpf}</Typography>
                <Typography variant="body2" color="text.secondary">{baixa.Evento}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>Valor: <b>{moeda(baixa.ValorTotal)}</b></Typography>
              </Box>
              <TextField type="date" size="small" label="Data do pagamento" value={dataPg}
                onChange={(e) => setDataPg(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField select size="small" label="Forma de pagamento" value={forma}
                onChange={(e) => setForma(e.target.value)} fullWidth>
                {Object.entries(FORMAS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>

              {/* Recebido pode ser menor que o cobrado — caso de desconto concedido. */}
              <CampoMoeda label="Valor recebido" value={valorPago} onChange={(n) => setValorPago(n)} fullWidth />
              {descontoBaixa() > 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Desconto de <b>{moeda(descontoBaixa())}</b> sobre {moeda(baixa.ValorTotal)}.
                </Alert>
              )}
              {descontoBaixa() !== 0 && (
                <TextField size="small" fullWidth multiline minRows={2} label="Motivo do valor diferente"
                  placeholder="Ex.: desconto concedido pela coordenação."
                  value={observacao} onChange={(e) => setObservacao(e.target.value)}
                  inputProps={{ maxLength: 300 }}
                  error={!observacao.trim()} helperText={!observacao.trim() ? "Obrigatório." : " "} />
              )}
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ textTransform: "none" }}>
                {arquivo ? arquivo.name : "Anexar comprovante (opcional)"}
                <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setArquivo(e.target.files?.[0] || null)} />
              </Button>
              <Typography variant="caption" color="text.secondary">PDF ou imagem (jpg, png, webp), até 8 MB.</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBaixa(null)}>Cancelar</Button>
          <Button variant="contained" startIcon={<PaidIcon />} onClick={confirmar} disabled={salvando === baixa?.Id}>
            {salvando === baixa?.Id ? "Confirmando…" : "Confirmar pagamento"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
